import type {
  IEffectivity,
  IFeature,
  IFeatureData,
  IFeaturesAssociations,
  IProcosState,
  IRule,
} from '../../types';
import { ERuleStrategy, IdFormat } from '../data/Constants';
import { appSettings } from '../settings';

const { RuleStrategy } = appSettings.ProcosSaveBatching || {};
/**
 * Checks if the given feature code ends with the LessFeature suffix
 * @param {string} code Feature or option code to check
 * @returns {boolean} True if the code ends with the LessFeature suffix, false otherwise
 */
export function isLessFeature(code: string): boolean {
  return code.endsWith(IdFormat.Suffix.LessFeature);
}

/**
 * Flips/inverts market associations (returns markets not in original)
 * @param {string[]} allMarkets All available markets
 * @param {string[]} originalMarkets Original markets array
 * @returns {string[]} Markets not in original array
 */
export const flipCountries = (allMarkets: string[], originalMarkets: string[]): string[] => {
  return allMarkets?.filter((market) => !originalMarkets?.includes(market));
};

/**
 * Method returns feature effectivity
 * @param {IFeatureData} features Features
 * @param {IProcosState} procos Procos state
 * @returns {IEffectivity[]} Feature effectivity array
 */
export const getFeatureEffectivityDataForPayload = (
  features: IFeatureData,
  procos: IProcosState
): IEffectivity[] => {
  const featureEffectivities: IEffectivity[] = [];
  const initialActiveStatusOfFeature = { ...procos.features.status.initial };
  const updatedActiveStatusOfFeature = { ...procos.features.status.updated };
  // when market association is not present for any feature, featureMA is not having data of feature updated

  Object.keys(initialActiveStatusOfFeature).forEach((key) => {
    const hasUpdatedStatus =
      initialActiveStatusOfFeature[key] !== updatedActiveStatusOfFeature[key];
    const featureCodes = key.split('.');
    const featureCode = featureCodes.length === 0 ? featureCodes[0] : featureCodes.at(-1);
    if (hasUpdatedStatus) {
      featureEffectivities.push({
        code: key,
        type: getFeatureType(features, featureCode),
        isActive: updatedActiveStatusOfFeature[key],
      });
    }
  });

  return featureEffectivities;
};

/**
 * Returns the family type of particular feature code
 * @param {IFeatureData} features Features
 * @param {string} featureCode Feature code
 * @returns {string} feature family type
 */
function getFeatureType(features: IFeatureData, featureCode: string): string {
  if (features.data) {
    const feature = features.data.find((ftr: IFeature) => ftr.code === featureCode);
    if (feature) {
      return feature.familyType;
    }
  }
  return '';
}

/**
 * To get the Feature Market Associations in a required Format
 * @param {IFeatureData} features features
 * @param {IFeaturesAssociations} featuresMA Updated Market Associations of features
 * @param {number} productMALength product market associations length
 * @param {string} allMarkets all available Markets
 * @returns {IRule[]} rules
 */
export const getFeatureMARulesForPayload = (
  features: IFeatureData,
  featuresMA: IFeaturesAssociations,
  productMALength: number,
  allMarkets: string[]
): IRule[] => {
  const featureMARules: IRule[] = [];
  Object.keys(featuresMA).forEach(function (key) {
    const featureCodes = key.split('.');
    const featureCode = featureCodes.length === 0 ? featureCodes[0] : featureCodes.at(-1);

    //New Association
    //Below criteria procos updates are handled
    // When initial Procos at feature/family level is present and there is a change done by user at procos other than
    // resetting to product level
    if (
      featuresMA[key].updatedMarkets.length < productMALength &&
      !isEqualAssociations(featuresMA[key].initialMarkets, featuresMA[key].updatedMarkets)
    ) {
      const featureMARule: IRule = {
        code: key,
        type: featuresMA[key].type ?? getFeatureType(features, featureCode),
        countries:
          RuleStrategy === ERuleStrategy.Exclusive
            ? flipCountries(allMarkets, featuresMA[key].updatedMarkets)
            : featuresMA[key].updatedMarkets,
      };
      if (RuleStrategy === ERuleStrategy.Exclusive) {
        featureMARule.includeMarkets = false;
      }
      featureMARules.push(featureMARule);
    } else if (
      featuresMA[key].updatedMarkets.length === productMALength &&
      featuresMA[key].initialMarkets.length > 0
    ) {
      //Delete the Rule which means send empty countries, when it needs to follow product level
      // Below condition is excecuted when market association at feature level is reset to product level
      // When feature/family does not have initial procos data
      // this is used for deleting existing procos information in ACE
      featureMARules.push({
        code: key,
        type: featuresMA[key].type ?? getFeatureType(features, featureCode),
        countries: [],
      });
    }
  });

  return featureMARules;
};

/**
 * Compares the given Array of countries and returns true if both are equal else false
 * @param {string[]} initialMarkets Initial markets array
 * @param {string[]} updatedMarkets Updated markets array
 * @returns {boolean} True if arrays are equal, false otherwise
 */
export function isEqualAssociations(initialMarkets: string[], updatedMarkets: string[]): boolean {
  const compareFunction = (a: string, b: string): number => a.localeCompare(b);

  const sortedInitialMarkets = initialMarkets.slice().sort(compareFunction);
  const sortedUpdatedMarkets = updatedMarkets.slice().sort(compareFunction);

  return (
    sortedInitialMarkets.length === sortedUpdatedMarkets.length &&
    sortedInitialMarkets.every((market, index) => market === sortedUpdatedMarkets[index])
  );
}

/**
 * Checks if the given market associations are part of product markets and returns the filtered array
 * @param {string[]} markets Markets to validate
 * @param {string[]} productMa Product market associations
 * @returns {string[]} Filtered valid markets
 */
export function fetchValidMarkets(markets: string[], productMa: string[]): string[] {
  const updatedMarkets: string[] = [];
  markets.forEach((market) => {
    const m = productMa?.find((x) => x === market);
    if (m) {
      updatedMarkets.push(market);
    }
  });
  if (updatedMarkets.length === 0) {
    return productMa;
  }
  return updatedMarkets;
}

/**
 * Add a market to all Features Market associations when market added at Product level
 * @param {IFeaturesAssociations} featuresMa Features market associations
 * @param {string} market Market code to add
 * @returns {IFeaturesAssociations} Updated features market associations
 */
export function addMarketFeatureMA(
  featuresMa: IFeaturesAssociations,
  market: string
): IFeaturesAssociations {
  Object.keys(featuresMa).forEach((key) => {
    featuresMa[key].updatedMarkets.push(market);
  });
  return featuresMa;
}

/**
 * To Revert the Feature Market Associations to initial Saved selections
 * @param {IFeaturesAssociations} featuresMA Features market associations
 * @param {string[]} productMa Product market associations
 * @returns {IFeaturesAssociations} Reverted features market associations
 */
export function revertFeaturesMA(
  featuresMA: IFeaturesAssociations,
  productMa: string[]
): IFeaturesAssociations {
  Object.keys(featuresMA).forEach((key) => {
    if (featuresMA[key].initialMarkets.length === 0) {
      delete featuresMA[key];
    } else {
      featuresMA[key].updatedMarkets = fetchValidMarkets(featuresMA[key].initialMarkets, productMa);
    }
  });

  return featuresMA;
}

/**
 * Formats a date string (assumed to be in a format parsable by `new Date()`)
 * into a specific "MM-DD-YYYY HH:MM:SS UTC" string.
 *
 * @param {string} dateValue The input date string to be formatted.
 * @returns {string} The formatted date string in "MM-DD-YYYY HH:MM:SS UTC" format.
 * Returns an invalid date string if the input cannot be parsed.
 */
export const formatDateString = (dateValue: string): string => {
  const date = new Date(dateValue);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const day = ('0' + date.getDate()).slice(-2);
  const year = date.getFullYear();
  const hour = ('0' + date.getHours()).slice(-2);
  const min = ('0' + date.getMinutes()).slice(-2);
  const seg = ('0' + date.getSeconds()).slice(-2);
  return month + '-' + day + '-' + year + ' ' + hour + ':' + min + ':' + seg + ' UTC';
};
